import { createSignal, For, onCleanup, onMount, Show } from 'solid-js'
import ConsentButton from './ConsentButton'
import ConsentCategoryRow from './ConsentCategoryRow'
import { CATEGORIES, COPY } from './copy'

export interface ConsentPreferencesModalProps {
  initial: { analytics: boolean; marketing: boolean }
  gpc: boolean
  busy: boolean
  /** shadow root (or document) used for focus management */
  root: Document | ShadowRoot
  onClose: () => void
  onReject: () => void
  onSave: (c: { analytics: boolean; marketing: boolean }) => void
  onAccept: () => void
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'

export default function ConsentPreferencesModal(props: ConsentPreferencesModalProps) {
  const [analytics, setAnalytics] = createSignal(props.initial.analytics)
  const [marketing, setMarketing] = createSignal(props.initial.marketing && !props.gpc)
  let dialog!: HTMLDivElement
  let heading!: HTMLHeadingElement

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      // dismiss without change; never treated as consent
      e.preventDefault()
      props.onClose()
      return
    }
    if (e.key !== 'Tab' || !dialog) return
    const items = [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)]
    if (!items.length) return
    const first = items[0]
    const last = items[items.length - 1]
    const active = props.root.activeElement
    if (e.shiftKey && (active === first || active === heading)) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && active === last) {
      e.preventDefault()
      first.focus()
    }
  }

  onMount(() => heading?.focus())
  onCleanup(() => {})

  return (
    <div class="tc-modal-layer fixed inset-0 flex items-end justify-center bg-black/60 sm:items-center" data-testid="consent-modal">
      <div
        ref={dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="consent-modal-title"
        aria-describedby="consent-modal-intro"
        aria-busy={props.busy ? 'true' : 'false'}
        on:keydown={onKey}
        class="fixed inset-0 flex flex-col bg-[var(--tc-surface)] text-[color:var(--tc-text)] sm:relative sm:inset-auto sm:max-h-[85vh] sm:w-full sm:max-w-[560px] sm:rounded-xl sm:border sm:border-[color:var(--tc-border)]"
      >
        <div class="flex items-start justify-between gap-4 p-4 md:p-5">
          <h2 id="consent-modal-title" ref={heading} tabindex="-1" class="m-0 text-base font-semibold outline-none">
            {COPY.modalTitle}
          </h2>
          <button
            type="button"
            aria-label={COPY.closeLabel}
            data-testid="consent-close"
            class="tc-close inline-flex h-[44px] w-[44px] items-center justify-center rounded-lg text-[color:var(--tc-text-2)]"
            on:click={() => props.onClose()}
          >
            <svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <path d="M3 3l10 10M13 3L3 13" />
            </svg>
          </button>
        </div>
        <div class="flex-1 overflow-y-auto px-4 md:px-5">
          <p id="consent-modal-intro" class="m-0 mb-2 text-sm text-[color:var(--tc-text-2)]">
            {COPY.modalIntro}
          </p>
          <Show when={props.gpc}>
            <p data-testid="consent-gpc-notice-modal" class="m-0 mb-2 text-[12px] text-[color:var(--tc-success)]">
              {COPY.gpc}
            </p>
          </Show>
          <fieldset class="m-0 border-0 p-0">
            <legend class="sr-only">{COPY.modalTitle}</legend>
            <For each={CATEGORIES}>
              {(c) => (
                <ConsentCategoryRow
                  category={c}
                  checked={c.id === 'analytics' ? analytics() : c.id === 'marketing' ? marketing() : true}
                  onChange={c.id === 'analytics' ? setAnalytics : c.id === 'marketing' ? setMarketing : undefined}
                  gpcLocked={props.gpc && c.id === 'marketing'}
                />
              )}
            </For>
          </fieldset>
        </div>
        <div class="sticky bottom-0 grid grid-cols-1 gap-2 border-t border-[color:var(--tc-border-soft)] bg-[var(--tc-surface)] p-4 pt-4 sm:grid-cols-3 md:p-5">
          <ConsentButton label={COPY.rejectAll} testId="consent-modal-reject" disabled={props.busy} onClick={props.onReject} />
          <ConsentButton
            label={COPY.saveChoices}
            testId="consent-modal-save"
            disabled={props.busy}
            onClick={() => props.onSave({ analytics: analytics(), marketing: marketing() && !props.gpc })}
          />
          <ConsentButton label={COPY.acceptAll} testId="consent-modal-accept" disabled={props.busy} onClick={props.onAccept} />
        </div>
      </div>
    </div>
  )
}
